import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './StudentFamilyPicker.module.css';

/** Searchable family field for student enroll / edit. */
export default function StudentFamilyPicker({ id, families, value, onChange, onAddNew }) {
  const listId = id ? `${id}-listbox` : undefined;
  const labelId = id ? `${id}-lbl` : undefined;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => families.find((f) => String(f.id) === String(value)),
    [families, value]
  );

  const selectedLabel = selected
    ? `${selected.first_name || ''} ${selected.last_name || ''} — #${selected.case_number || ''}`.trim()
    : '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return families;
    return families.filter((f) => {
      const hay = `${f.first_name || ''} ${f.last_name || ''} ${f.case_number ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [families, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const showInputValue = open ? query : selectedLabel;

  return (
    <div className={styles.familyCombo} ref={wrapRef}>
      <div className={styles.familyComboLabelRow}>
        <span className={styles.familyComboLabelText} id={labelId}>
          Family
        </span>
        <button type="button" className={styles.familyComboAddNew} onClick={onAddNew}>
          Add new family
        </button>
      </div>
      <div className={styles.familyComboControl}>
        {open && selected ? (
          <p className={styles.familyComboHint}>
            Current selection — type to narrow the list or pick another.
          </p>
        ) : null}
        <input
          type="text"
          className={styles.familyComboInput}
          id={id}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-labelledby={labelId}
          placeholder={
            !open && selectedLabel
              ? ''
              : open && selectedLabel
                ? 'Filter by name or case #…'
                : 'Search by name or case #…'
          }
          value={showInputValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              e.preventDefault();
            }
          }}
        />
        {open ? (
          <ul className={styles.familyComboList} id={listId} role="listbox">
            {filtered.length === 0 ? (
              <li className={styles.familyComboEmpty}>No families match. Try another search or add new.</li>
            ) : (
              filtered.map((f) => {
                const label = `${f.first_name || ''} ${f.last_name || ''} — #${f.case_number || ''}`.trim();
                const isSel = String(f.id) === String(value);
                return (
                  <li key={f.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      className={[styles.familyComboOption, isSel ? styles.familyComboOptionSelected : ''].join(
                        ' '
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onChange(String(f.id));
                        setOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
